/* YNEW: Kontakt-Widget von Jira Service Management (iframe #jsd-widget, eingebunden in
   layout/theme.liquid): Button-Stil der jeweiligen Firma und deutsche Texte.
   embed.js schreibt das iframe ohne src (same-origin) bei DOMContentLoaded, darum lässt es sich von
   hier aus stylen und übersetzen.

   Stil: Das Widget ist eines für alle Firmen; eine Farbe in den Jira-Einstellungen gälte überall
   gleich. Ins iframe kommt ein <style> mit dem gemessenen Stil eines Standard-Buttons der Seite
   (inkl. assets/domain-<domain>.css), Schrift und Linkfarbe sowie den @font-face-Regeln der
   Firmenschrift, denn Styles und Schriften erben nicht ins iframe. Die Grundfarbe aus Jira kommt als <style id="widget-theme"> mit
   !important nach unserem Style, darum :root vor jedem Selektor (höhere Spezifität). Die
   iframe-Größe passt embed.js an den Inhalt an.

   Texte: Atlassian liefert Hinweise, Meldungen und Feldnamen auf Englisch. Ein MutationObserver im
   iframe ersetzt sie nach Wortlaut (nicht nach DOM-Position, die ändert Atlassian), die deutschen
   Texte kommen aus locales (ynew.contact_widget.*) über snippets/ynew-jsd-widget-texts.liquid. */
(() => {
  const STYLE_ID = 'ynew-jsd-widget-style';

  // Originaltexte aus https://jsd-widget.atlassian.com/assets/iframe.js bzw. Feldnamen aus Jira
  // → Schlüssel in locales (ynew.contact_widget.*). Verglichen wird ohne Leerzeichen am Rand.
  const SOURCES = {
    'Help Center Widget Content': 'region', // aria-label des Formularbereichs
    'Required fields are marked with an asterisk *': 'required_fields',
    Summary: 'summary',
    Description: 'description',
    'Your contact e-mail': 'email',
    Attachment: 'attachment',
    'File size should not exceed': 'max_file_size', // danach „5" und „MB" als eigene Textknoten
    'Drop files to attach or': 'drop_files',
    browse: 'browse',
    Send: 'send',
    Close: 'close',
    'Upload file': 'upload_file',
    Upload: 'upload',
    required: 'required', // title am Sternchen
    'This field is required.': 'field_required',
    'Enter a valid email address.': 'invalid_email',
    'Enter 255 characters or fewer.': 'too_long',
    'File upload failed': 'upload_failed_title',
    'There was an error uploading your file. Please try again.': 'upload_failed_text',
    'Network error': 'network_error_title',
    'There was a network issue while uploading your file. Please check your connection and try again.':
      'network_error_text',
    'Can’t add attachment': 'attachment_error_title',
    "Can't add attachment": 'attachment_error_title',
    'Number of attachments cannot exceed 20': 'too_many_attachments',
    'Uploaded file is larger than 5 MB': 'file_too_large',
    'Thanks!': 'thanks',
    "We've received your request and will get back to you shortly.": 'confirmation',
    'We’re not sure what went wrong. Please try again later.': 'error_generic',
  };
  const ATTRIBUTES = ['aria-label', 'title', 'placeholder'];

  let texts = {};
  try {
    texts = JSON.parse(document.getElementById('ynew-jsd-widget-texts').textContent);
  } catch (e) {
    // ohne Snippet bleibt das Widget englisch
  }

  // Übersetzung eines Textes oder null, Leerzeichen am Rand bleiben erhalten
  function translate(value) {
    const source = value.trim();
    if (!source) return null;
    let text = texts[SOURCES[source]];
    if (!text && /^\d+ of the uploaded files (?:is|are) larger than 5 MB$/.test(source)) text = texts.file_too_large;
    if (!text && source.startsWith('Remove ') && texts.remove) text = texts.remove + source.slice(6); // aria-label „Remove <Datei>"
    return text && text !== source ? value.replace(source, () => text) : null;
  }

  function translateTree(root) {
    const doc = root.ownerDocument;
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = translate(node.nodeValue);
      if (text !== null) node.nodeValue = text;
    }
    for (const el of root.querySelectorAll(ATTRIBUTES.map((a) => `[${a}]`).join(','))) {
      for (const attr of ATTRIBUTES) {
        const text = el.hasAttribute(attr) ? translate(el.getAttribute(attr)) : null;
        if (text !== null) el.setAttribute(attr, text);
      }
    }
  }

  // Das Formular rendert React erst beim Öffnen, Meldungen erst bei Bedarf: bei jeder Änderung den
  // (kleinen) Widget-Baum erneut durchgehen. Eigene Ersetzungen lösen genau einen leeren Durchlauf aus.
  const watched = new WeakSet();
  function watchTexts(doc) {
    const body = doc.body;
    if (!body || watched.has(body)) return;
    watched.add(body);
    translateTree(body);
    new doc.defaultView.MutationObserver(() => translateTree(body)).observe(body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRIBUTES,
    });
  }

  // Erste Familie einer font-family-Liste, ohne Anführungszeichen
  const firstFamily = (list) => list.split(',')[0].trim().replace(/^["']|["']$/g, '');

  // Regeln eines Stylesheets; Stylesheets einer fremden Domain sind nicht lesbar
  function readRules(sheet) {
    try {
      return sheet.cssRules;
    } catch (e) {
      return [];
    }
  }

  // @font-face-Regeln einer Familie aus den Stylesheets der Seite, auch aus @import (domain-*.css
  // binden ynew-brand-base.css so ein), @media, @supports und @layer. URLs absolut machen: im iframe
  // gälten relative URLs relativ zur Seite statt zur CSS-Datei.
  function fontFaces(family) {
    const rules = [];
    const collect = (cssRules, base) => {
      for (const rule of cssRules) {
        if (rule instanceof CSSImportRule) {
          if (rule.styleSheet) collect(readRules(rule.styleSheet), rule.styleSheet.href || base);
        } else if (rule instanceof CSSFontFaceRule) {
          if (firstFamily(rule.style.getPropertyValue('font-family')) !== family) continue;
          rules.push(rule.cssText.replace(/url\((['"]?)([^'")]+)\1\)/g, (m, q, url) => `url("${new URL(url, base).href}")`));
        } else if (rule.cssRules) {
          collect(rule.cssRules, base);
        }
      }
    };
    for (const sheet of document.styleSheets) collect(readRules(sheet), sheet.href || document.baseURI);
    return rules.join('\n');
  }

  // So sieht ein Standard-Button im Shop aus: unsichtbaren <button class="button"> messen. Damit
  // greifen auch Firmen-CSS, die Buttons per Selektor statt über Variablen färben (Cornelsen, Zollner).
  function buttonStyle() {
    const probe = document.createElement('button');
    probe.type = 'button';
    probe.className = 'button';
    probe.tabIndex = -1;
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position: absolute; visibility: hidden; pointer-events: none;';
    document.body.appendChild(probe);
    const s = getComputedStyle(probe);
    const style = {
      background: s.backgroundColor,
      gradient: s.backgroundImage,
      color: s.color,
      radius: s.borderRadius,
      font: s.fontFamily,
      weight: s.fontWeight,
    };
    probe.remove();
    return style;
  }

  function css() {
    const body = getComputedStyle(document.body);
    const button = buttonStyle();
    const link = body.getPropertyValue('--color-link').trim() || '28, 112, 190';
    const families = [...new Set([firstFamily(body.fontFamily), firstFamily(button.font)])];

    return `${families.map(fontFaces).join('\n')}
:root {
  --ynew-button: ${button.background};
  --ynew-button-gradient: ${button.gradient};
  --ynew-button-text: ${button.color};
  --ynew-button-radius: ${button.radius};
  --ynew-button-font: ${button.font};
  --ynew-button-weight: ${button.weight};
  --ynew-link: rgb(${link});
  --ynew-font: ${body.fontFamily};
}
/* Schwebender Button „Kontaktformular", Kopfzeile des Formulars und „Senden" */
:root #help-button,
:root #widget-header,
:root #submit-button {
  background-color: var(--ynew-button) !important;
  background-image: var(--ynew-button-gradient) !important;
}
:root #help-button,
:root #submit-button {
  border-radius: var(--ynew-button-radius) !important;
}
:root #help-button *,
:root #widget-header h3 *,
:root #widget-header button,
:root #submit-button,
:root #submit-button * {
  color: var(--ynew-button-text) !important;
  font-family: var(--ynew-button-font) !important;
}
:root #help-button *,
:root #submit-button,
:root #submit-button * {
  font-weight: var(--ynew-button-weight) !important;
}
:root .help-form *:not(#submit-button, #submit-button *) {
  font-family: var(--ynew-font) !important;
}
/* „browse" im Anhang-Feld ist ein span[role=button] in Atlassian-Blau; unterstrichen, weil die
   Linkfarbe mancher Firmen (PwC) der Textfarbe entspricht */
:root .help-form span[role='button'] {
  color: var(--ynew-link) !important;
  text-decoration: underline !important;
}`;
  }

  function apply(iframe) {
    const doc = iframe.contentDocument;
    if (!doc || !doc.head) return;
    let style = doc.getElementById(STYLE_ID);
    if (!style) {
      style = doc.createElement('style');
      style.id = STYLE_ID;
      doc.head.appendChild(style);
    }
    style.textContent = css();
    watchTexts(doc);
  }

  function init() {
    const iframe = document.getElementById('jsd-widget');
    if (!iframe) {
      // embed.js legt das iframe erst bei DOMContentLoaded an
      const observer = new MutationObserver(() => {
        if (!document.getElementById('jsd-widget')) return;
        observer.disconnect();
        init();
      });
      observer.observe(document.body, { childList: true });
      return;
    }
    apply(iframe);
    // Erneut, falls embed.js das iframe-Dokument neu schreibt oder Firmen-CSS später lädt
    iframe.addEventListener('load', () => apply(iframe));
    window.addEventListener('load', () => apply(iframe), { once: true });
  }

  init();
})();
